import type { PayinSocketPayload } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import {
  type PayinBrebChannel,
  PayinRail,
  type PayinRuntimeEntity,
  PayinRuntimeStatus,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";

/** A payment that landed on one of the organization's keys, through its any-amount QR or the key typed in. */
export interface BrebKeyIncomingPayment {
  id: string;
  /** What the payer sent, as a decimal string. */
  grossAmount: string;
  /** What reached the wallet after the commission; null when the payin does not say. */
  netAmount: string | null;
  payerName: string | null;
  payerBankName: string | null;
  /** When it was credited, or when it was detected for a payin with no confirmation stamp. */
  receivedAt: string;
  /** The key row it reached; null on older payins, which only name the value. */
  receivingKeyId: string | null;
  receivingKeyValue: string | null;
}

/**
 * The two ways money reaches the key with no collection of its own behind it: its any-amount QR, or the key
 * typed in a banking app. A QR with an amount (`QR`) is a collection with its own screen, which already says
 * when it was paid.
 */
const KEY_CHANNELS: ReadonlySet<PayinBrebChannel> = new Set(["STATIC_QR", "KEY"]);

/**
 * Whether a `payin:updated` push is worth reading: a Bre-B collection that was just credited, in the
 * organization on screen. Staff receive every organization's pushes, so the organization is checked here when
 * it is known; the read that follows decides whether the payment reached THIS key.
 */
export function isIncomingPaymentCandidate(
  payload: Pick<PayinSocketPayload, "rail" | "status" | "currency" | "organization_id">,
  organizationId: string | null,
): boolean {
  return (
    payload.rail === PayinRail.BREB &&
    payload.status === PayinRuntimeStatus.CONFIRMED &&
    payload.currency === "COP" &&
    (!organizationId || payload.organization_id === organizationId)
  );
}

/**
 * The payment a payin represents on any of the organization's keys, or null when it is not one: still unpaid,
 * another rail, or a collection with its own QR.
 */
export function keyPaymentFor(payin: PayinRuntimeEntity): BrebKeyIncomingPayment | null {
  if (payin.rail !== PayinRail.BREB || payin.status !== PayinRuntimeStatus.CONFIRMED) return null;
  const breb = payin.breb;
  if (!breb?.channel || !KEY_CHANNELS.has(breb.channel)) return null;
  const grossAmount = payin.gross_amount_received ?? payin.gross_amount_expected;
  if (!grossAmount) return null;
  return {
    id: payin.id,
    grossAmount,
    netAmount: payin.net_amount,
    payerName: breb.payer_name,
    payerBankName: breb.payer_bank_name,
    receivedAt: payin.confirmed_at ?? payin.created_at,
    receivingKeyId: breb.receiving_key_id ?? null,
    receivingKeyValue: breb.receiving_key,
  };
}

/**
 * Whether a payment reached this key. The key id decides when the payin names one; the value covers older
 * rows and a backend that predates key ids.
 */
export function reachedKey(
  payment: Pick<BrebKeyIncomingPayment, "receivingKeyId" | "receivingKeyValue">,
  receivingKey: Pick<BrebReceivingKey, "id" | "key_value">,
): boolean {
  return (
    (!!payment.receivingKeyId && payment.receivingKeyId === receivingKey.id) ||
    payment.receivingKeyValue === receivingKey.key_value
  );
}

/** The payment a payin represents for the key on screen, or null when it is not one (see `keyPaymentFor`). */
export function incomingPaymentFor(
  payin: PayinRuntimeEntity,
  receivingKey: Pick<BrebReceivingKey, "id" | "key_value">,
): BrebKeyIncomingPayment | null {
  const payment = keyPaymentFor(payin);
  return payment && reachedKey(payment, receivingKey) ? payment : null;
}

/** Newest first, one entry per payin, capped: a cashier's screen, not a history. */
export function withIncomingPayment(
  payments: readonly BrebKeyIncomingPayment[],
  payment: BrebKeyIncomingPayment,
  limit: number,
): BrebKeyIncomingPayment[] {
  if (payments.some((existing) => existing.id === payment.id)) return [...payments];
  return [payment, ...payments].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt)).slice(0, limit);
}
